import { Radio, Stack } from '@mantine/core'
import { formatClock } from '@racedash/shared'
import { formatDuration, readClock } from '@racedash/shared'
import type { ClockCheckpoint } from '@racedash/shared'
import { Datum, NoValue, Tile } from '../Tile'
import type { WidgetEditorProps, WidgetProps } from './props'

const labels = {
  remaining: 'Remaining',
  elapsed: 'Elapsed',
  timeOfDay: 'Time',
} as const

/**
 * The session clock, counted on from the last checkpoint the interpreter wrote.
 *
 * Not from a start time and a duration, which would be simpler and would be
 * wrong for the rest of the race the first time the clock stopped. When the
 * checkpoint says the clock is not running this holds still, which is what a
 * red flag should look like.
 *
 * Time of day is the exception, and does not stop when the session does. It
 * still comes from the corrected clock rather than the browser's raw one,
 * because a laptop on a pit wall is precisely the machine whose clock is
 * quietly several minutes out.
 */
export default function Clock({ config, data, now }: WidgetProps<'clock'>) {
  /*
   * Time of day is the one reading here that must never freeze.
   *
   * It is a wall clock. It answers "what time is it", which has an answer
   * whether or not a session is running, and a wall clock stopped at the
   * chequered flag is simply broken — it was reading 14:00 at ten to five.
   *
   * So it counts from clock.now(): the viewer's own clock, offset by the skew
   * measured against the timestamps the capture job writes. Once capture
   * stops, that skew stops being re-measured, which is right — it is a fixed
   * offset, and time keeps passing on both clocks.
   *
   * Elapsed and remaining are the opposite case and use asAt, which does
   * freeze. They are properties of the session rather than of the day, and a
   * countdown left over from an abandoned practice session ticking down for
   * ever is the fault asAt exists to fix.
   */
  if (config.show === 'timeOfDay') {
    return (
      <Tile label={labels.timeOfDay}>
        <Datum
          value={formatClock(data.clock.now(now), { seconds: true })}
          as="time"
        />
      </Tile>
    )
  }

  const raw = data.session?.clock
  const checkpoint: ClockCheckpoint | null = raw?.at
    ? {
        at: raw.at.toDate(),
        elapsedMs: raw.elapsedMs,
        remainingMs: raw.remainingMs,
        running: raw.running,
      }
    : null
  const clock = readClock(checkpoint, data.asAt(now))

  if (!clock) {
    return (
      <Tile label={labels[config.show]}>
        <NoValue as="time" />
      </Tile>
    )
  }

  const ms = config.show === 'remaining' ? clock.remainingMs : clock.elapsedMs

  return (
    <Tile label={labels[config.show]}>
      <Datum
        value={formatDuration(ms)}
        as="time"
        // Said rather than implied. A countdown that has stopped moving looks
        // exactly like one the page has stopped updating.
        sub={clock.running ? undefined : 'stopped'}
      />
    </Tile>
  )
}

export function Editor({ config, onChange }: WidgetEditorProps<'clock'>) {
  return (
    <Radio.Group
      value={config.show}
      onChange={(v) => onChange({ kind: 'clock', show: v as 'remaining' | 'elapsed' | 'timeOfDay' })}
    >
      <Stack gap="xs" mt="xs">
        <Radio value="remaining" label="Time remaining" />
        <Radio value="elapsed" label="Time elapsed" />
        <Radio value="timeOfDay" label="Time of day" />
      </Stack>
    </Radio.Group>
  )
}
